// Call Duration vs. Handoff Signal — scatter + outlier table.
//
// X-axis: session.duration (seconds). Y-axis: session.noOfTotalMessages.
// Point colored by interactionOutcome. The quadrants:
//   - top-right (long + many msgs) = heavy conversation, legitimate work
//   - bottom-right (long + few msgs) = dead air / stuck bot
//   - top-left (short + many msgs) = fast rapid-fire thrash
//   - bottom-left (short + few msgs) = quick hangup
//
// Escalated sessions are called out with a filled marker. The outlier table
// surfaces the worst "long call + escalated" entries — the ones to drill
// into with the per-session Audit view.
//
// (True "time-to-first-handoff tool invocation" needs per-session message
// fan-out; this proxy uses only fields already on contactAIAgentSession.)

import React, { useMemo, useState } from 'react';
import { Badge, Group, Paper, Stack, Table, Text, Title } from '@mantine/core';
import { fmtDuration } from '../utils';

const COLORS = {
  HANDLED: '#12b886',
  COMPLETED: '#12b886',
  ESCALATED: '#228be6',
  ABANDONED: '#fd7e14',
  FAILED: '#fa5252',
  _OTHER: '#adb5bd',
};

const WIDTH = 760;
const HEIGHT = 320;
const PAD = { top: 16, right: 16, bottom: 36, left: 48 };

export function CallDurationVsHandoff({ data }) {
  const [hovered, setHovered] = useState(null);

  const points = useMemo(() => {
    const list = [];
    for (const s of data.sessions ?? []) {
      if (s.duration == null || s.noOfTotalMessages == null) continue;
      const outcome = s.interactionOutcome ?? '_OTHER';
      list.push({
        key: s.sessionId ?? s.interactionId ?? Math.random(),
        bot: s.agentName ?? '(unnamed)',
        outcome,
        color: COLORS[outcome] ?? COLORS._OTHER,
        filled: outcome === 'ESCALATED',
        durationSec: Math.round(s.duration / 1000),
        messages: s.noOfTotalMessages,
        avgResponseMs: s.averageResponseTime ?? null,
        startTime: s.sessionStartTime,
        interactionId: s.interactionId,
      });
    }
    return list;
  }, [data.sessions]);

  const { xMax, yMax } = useMemo(() => {
    let xMax = 60, yMax = 10;
    for (const p of points) {
      if (p.durationSec > xMax) xMax = p.durationSec;
      if (p.messages > yMax) yMax = p.messages;
    }
    return { xMax: nextNice(xMax), yMax: nextNice(yMax) };
  }, [points]);

  const chartW = WIDTH - PAD.left - PAD.right;
  const chartH = HEIGHT - PAD.top - PAD.bottom;
  const xScale = (sec) => PAD.left + (sec / xMax) * chartW;
  const yScale = (msg) => PAD.top + chartH - (msg / yMax) * chartH;

  const legend = Object.entries({
    HANDLED: 'Handled', ESCALATED: 'Escalated', ABANDONED: 'Abandoned', FAILED: 'Failed', _OTHER: 'Other',
  }).map(([k, label]) => ({ color: COLORS[k], label }));

  const outliers = useMemo(() => {
    // Escalated sessions ordered by duration desc — the "worst bot thrash
    // before giving up" candidates.
    return [...points]
      .filter((p) => p.outcome === 'ESCALATED')
      .sort((a, b) => b.durationSec - a.durationSec)
      .slice(0, 10);
  }, [points]);

  const summary = useMemo(() => {
    const outcomes = {};
    for (const p of points) outcomes[p.outcome] = (outcomes[p.outcome] ?? 0) + 1;
    return outcomes;
  }, [points]);

  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="sm">
        <div>
          <Title order={5}>Call duration vs. message count</Title>
          <Text size="xs" c="dimmed">
            Each dot is one session. Filled dot = escalated to human. Bottom-right (long call, few messages)
            and top-left (short call, many messages) are the thrash quadrants worth drilling into.
          </Text>
        </div>

        <Group gap="md">
          {legend.map((l) => (
            <Group key={l.label} gap={4}>
              <span style={{ display: 'inline-block', width: 10, height: 10, background: l.color, borderRadius: '50%' }} />
              <Text size="xs" c="dimmed">{l.label} ({summary[l.label.toUpperCase()] ?? summary._OTHER ?? 0})</Text>
            </Group>
          ))}
        </Group>

        {points.length === 0 ? (
          <Text size="sm" c="dimmed">No scored sessions in window.</Text>
        ) : (
          <div style={{ overflowX: 'auto', position: 'relative' }}>
            <svg width={WIDTH} height={HEIGHT} style={{ display: 'block', background: '#fafafa', borderRadius: 4 }}>
              {/* axes */}
              <line x1={PAD.left} y1={PAD.top + chartH} x2={PAD.left + chartW} y2={PAD.top + chartH} stroke="#adb5bd" />
              <line x1={PAD.left} y1={PAD.top} x2={PAD.left} y2={PAD.top + chartH} stroke="#adb5bd" />

              {/* x ticks */}
              {ticks(xMax, 5).map((t) => (
                <g key={`xt-${t}`}>
                  <line x1={xScale(t)} y1={PAD.top + chartH} x2={xScale(t)} y2={PAD.top + chartH + 4} stroke="#adb5bd" />
                  <text x={xScale(t)} y={PAD.top + chartH + 16} fontSize="10" textAnchor="middle" fill="#495057">
                    {t >= 60 ? `${Math.round(t / 60)}m` : `${t}s`}
                  </text>
                </g>
              ))}
              <text x={PAD.left + chartW / 2} y={HEIGHT - 4} fontSize="11" textAnchor="middle" fill="#495057">
                session duration
              </text>

              {/* y ticks */}
              {ticks(yMax, 4).map((t) => (
                <g key={`yt-${t}`}>
                  <line x1={PAD.left - 4} y1={yScale(t)} x2={PAD.left} y2={yScale(t)} stroke="#adb5bd" />
                  <text x={PAD.left - 6} y={yScale(t) + 3} fontSize="10" textAnchor="end" fill="#495057">
                    {t}
                  </text>
                </g>
              ))}
              <text x={10} y={PAD.top + chartH / 2} fontSize="11" fill="#495057" transform={`rotate(-90, 10, ${PAD.top + chartH / 2})`} textAnchor="middle">
                messages
              </text>

              {/* points */}
              {points.map((p) => (
                <circle
                  key={p.key}
                  cx={xScale(p.durationSec)}
                  cy={yScale(p.messages)}
                  r={p.filled ? 4.5 : 3.5}
                  fill={p.filled ? p.color : '#ffffff'}
                  stroke={p.color}
                  strokeWidth={1.5}
                  opacity={hovered && hovered.key !== p.key ? 0.25 : 0.85}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => setHovered(p)}
                  onMouseLeave={() => setHovered(null)}
                />
              ))}

              {/* hover readout */}
              {hovered && (
                <g>
                  <rect
                    x={Math.min(xScale(hovered.durationSec) + 8, WIDTH - 180)}
                    y={Math.max(yScale(hovered.messages) - 42, 4)}
                    width={170}
                    height={38}
                    rx={3}
                    fill="#212529"
                    opacity={0.9}
                  />
                  <text
                    x={Math.min(xScale(hovered.durationSec) + 14, WIDTH - 174)}
                    y={Math.max(yScale(hovered.messages) - 26, 20)}
                    fontSize="11"
                    fill="#f8f9fa"
                  >
                    {hovered.bot} · {hovered.outcome}
                  </text>
                  <text
                    x={Math.min(xScale(hovered.durationSec) + 14, WIDTH - 174)}
                    y={Math.max(yScale(hovered.messages) - 12, 34)}
                    fontSize="10"
                    fill="#dee2e6"
                  >
                    {fmtDuration(hovered.durationSec * 1000)} · {hovered.messages} msgs
                  </text>
                </g>
              )}
            </svg>
          </div>
        )}

        {outliers.length > 0 && (
          <div>
            <Title order={6} mt="sm">Longest escalated sessions (potential thrash)</Title>
            <Text size="xs" c="dimmed" mb={4}>
              Top 10 escalated sessions by duration. These are the first candidates to open in the per-session Audit view.
            </Text>
            <Table verticalSpacing="xs" highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Bot</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Duration</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Messages</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Avg response</Table.Th>
                  <Table.Th>Interaction</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {outliers.map((o) => (
                  <Table.Tr key={o.key}>
                    <Table.Td><Text size="sm">{o.bot}</Text></Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Badge size="sm" color={o.durationSec > 300 ? 'red' : o.durationSec > 120 ? 'orange' : 'gray'} variant="light">
                        {fmtDuration(o.durationSec * 1000)}
                      </Badge>
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}><Text size="sm">{o.messages}</Text></Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Text size="sm">{o.avgResponseMs != null ? `${(o.avgResponseMs / 1000).toFixed(1)}s` : '—'}</Text>
                    </Table.Td>
                    <Table.Td><Text size="xs" c="dimmed" style={{ fontFamily: 'monospace' }}>{o.interactionId}</Text></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </div>
        )}
      </Stack>
    </Paper>
  );
}

function nextNice(n) {
  if (n <= 10) return 10;
  const pow = Math.pow(10, Math.floor(Math.log10(n)));
  const scaled = n / pow;
  if (scaled <= 1) return 1 * pow;
  if (scaled <= 2) return 2 * pow;
  if (scaled <= 5) return 5 * pow;
  return 10 * pow;
}
function ticks(max, n) {
  const step = max / n;
  const result = [];
  for (let i = 0; i <= n; i++) result.push(Math.round(step * i));
  return result;
}
